import { StatusLabelVariant } from '@sdir/sds';
import { LangMap, NormalizedStatus } from './types';

/**
 * Rekkefølgen vi faller tilbake på når språkkartet mangler valgt språk.
 * Klienten bruker locale 'nb', mens kravtjenesten merker norsk tekst med 'no'.
 */
const NORWEGIAN_KEYS = ['no', 'nb', 'nn'];

/**
 * Plukker tekst fra et språkkart ut fra locale, med fallback.
 * Samme mønster som GetLocalizedStringFromLanguageComponent i helpers/Certificates.
 */
export const pickLangText = (map?: LangMap, locale?: string): string => {
  if (!map) return '';

  const candidates =
    locale === 'en'
      ? ['en', ...NORWEGIAN_KEYS]
      : [locale || '', ...NORWEGIAN_KEYS, 'en'].filter((k) => !!k);

  const hit = candidates.map((key) => map[key]).find((value) => !!value);
  if (hit) return hit;

  // Ukjent språkkode i kartet — vis heller noe enn ingenting.
  const fallback = Object.keys(map)
    .map((key) => map[key])
    .find((value) => !!value);
  return fallback || '';
};

/**
 * Motoren sender status som streng ("Pass"), men tåler også enum-tall dersom
 * JsonStringEnumConverter skulle være av. Ukjent verdi tolkes som uavklart.
 */
const NUMERIC_ORDER: NormalizedStatus[] = ['pass', 'fail', 'inconclusive'];

export const normalizeStatus = (value: unknown): NormalizedStatus => {
  if (typeof value === 'number') return NUMERIC_ORDER[value] || 'inconclusive';

  const text = String(value ?? '').toLowerCase();
  if (text.startsWith('pass')) return 'pass';
  if (text.startsWith('fail')) return 'fail';
  return 'inconclusive';
};

/** Tekstnøkler for status. Uavklart er en egen tilstand — ikke en feil. */
export const STATUS_MESSAGE_IDS: { [key in NormalizedStatus]: string } = {
  pass: 'certreq.status.pass',
  fail: 'certreq.status.fail',
  inconclusive: 'certreq.status.inconclusive',
};

/** Statusfarge i CheckIndicator, samme visuelle språk som kvalifikasjonskortene. */
export const STATUS_INDICATORS: { [key in NormalizedStatus]: 'valid' | 'error' | 'unknown' } = {
  pass: 'valid',
  fail: 'error',
  inconclusive: 'unknown',
};

/**
 * Fargen på «Dokumentert»-merket.
 *
 * Bevisst `secondary` (sjøblå med ramme): den er visuelt forskjellig fra alle
 * tre motorstatusene — grønn (oppfylt), rød (ikke oppfylt) og gul/info (kan
 * ikke vurderes) — fordi opplastet dokumentasjon IKKE er en motorstatus.
 * Motoren vurderer bare registerdata; filen går til saksbehandler, som kan
 * avvise den. Grønt her ville fått søkeren til å tro at hen er kvalifisert.
 *
 * `primary` ble valgt bort fordi den er nesten hvit uten ramme, og forsvinner
 * mot de fargede kravkortene.
 */
export const DOCUMENTED_LABEL_VARIANT: StatusLabelVariant = 'secondary';
